import React, { useEffect } from "react";
import { Box, Chip, Skeleton, Typography, styled } from "@mui/material";
import { Link } from "react-router-dom";
import { ProductRelation } from "../../types/types";
import { useGetItemByIdQuery } from "../../services/items/items";
import { RELATION_LABELS } from "../../pages/shared/constants";
import { resolveAssetUrl } from "../../utils/assets";
import { useSkeletonAnimation } from "../../hooks/useSkeletonAnimation";

const CardLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  color: "inherit",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: 140,
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  transition: "box-shadow 0.2s",
  "&:hover": {
    boxShadow: theme.shadows[3],
  },
}));

const Thumbnail = styled("img")({
  width: 80,
  height: 80,
  objectFit: "contain",
  borderRadius: 4,
});

interface RelatedProductCardProps {
  relation: ProductRelation;
  /**
   * Reports whether this card ended up rendering, once its query settles (EB-520).
   *
   * The card is the only place that knows — it already fetches the product — but the *heading*
   * belongs to `RelatedProducts`, which would otherwise be left over an empty row. Passing the
   * answer up is what lets the parent drop the whole section when nothing survives.
   */
  onResolved?: (id: string, visible: boolean) => void;
}

const RelatedProductCard: React.FC<RelatedProductCardProps> = ({ relation, onResolved }) => {
  const { data: product, isLoading, isError } = useGetItemByIdQuery(relation.id);
  const animation = useSkeletonAnimation();

  /**
   * Hidden once resolved unless the target is genuinely active (EB-520).
   *
   * `is_active !== true` is strict on purpose, matching `listItems.mjs` and `ProductDetailPage`'s
   * own guard — all three have to agree, or a card links somewhere the detail page refuses to show.
   * Since EB-503 that destination renders "Producto no encontrado", so an unchecked card is a dead
   * link inside the shop.
   *
   * A missing or errored product is hidden by the same expression: it cannot be active, and a card
   * that cannot resolve is no more useful to a shopper than an inactive one.
   */
  const isHidden = !isLoading && product?.is_active !== true;

  useEffect(() => {
    if (!isLoading) onResolved?.(relation.id, !isHidden);
  }, [isLoading, isHidden, relation.id, onResolved]);

  // Still loading keeps today's placeholder card: the section is below the fold, and blanking it
  // on every navigation would be a bigger change than this ticket asks for.
  if (isHidden) return null;

  const name = isError || !product ? relation.id : product.name;

  const picture = resolveAssetUrl(product?.picture);

  return (
    <CardLink to={`/producto/${relation.id}`}>
      {isLoading ? (
        <Skeleton animation={animation} variant="rounded" width={80} height={80} />
      ) : picture ? (
        <Thumbnail src={picture} alt={name} />
      ) : (
        <Box width={80} height={80} display="flex" alignItems="center" justifyContent="center">
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        </Box>
      )}
      {isLoading ? (
        <Skeleton animation={animation} variant="text" width={104} height={18} sx={{ mt: 0.5 }} />
      ) : (
        <Typography
          variant="caption"
          fontWeight="bold"
          textAlign="center"
          mt={0.5}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {name}
        </Typography>
      )}
      <Chip label={RELATION_LABELS[relation.tipo_relacion]} size="small" sx={{ mt: 0.5 }} />
    </CardLink>
  );
};

export default RelatedProductCard;
